import { clamp } from "./utils.js";

export const QUALITY_PROFILES = Object.freeze([
  Object.freeze({
    id: "low",
    label: "Low",
    dprCap: 1,
    particleCap: 60_000,
    depthOfField: false,
  }),
  Object.freeze({
    id: "balanced",
    label: "Balanced",
    dprCap: 1.25,
    particleCap: 90_000,
    depthOfField: false,
  }),
  Object.freeze({
    id: "high",
    label: "High",
    dprCap: 1.5,
    particleCap: 120_000,
    depthOfField: true,
  }),
  Object.freeze({
    id: "ultra",
    label: "Ultra",
    dprCap: 1.65,
    particleCap: 150_000,
    depthOfField: true,
  }),
]);

/**
 * Runtime quality controller with deliberately slow hysteresis. It changes
 * drawRange and render resolution only; model buffers are never reallocated.
 */
export class QualityManager {
  constructor({
    isWebGL2 = true,
    hardwareConcurrency = 8,
    deviceMemory = 8,
    viewportWidth = 1280,
    requestedParticles = 100_000,
    startedAt = 0,
    warmupMs = 3_500,
    cooldownMs = 4_500,
    downgradeSamples = 3,
    upgradeSamples = 9,
  } = {}) {
    this.isWebGL2 = isWebGL2;
    this.hardwareConcurrency = Number.isFinite(hardwareConcurrency)
      ? hardwareConcurrency
      : 8;
    this.deviceMemory = Number.isFinite(deviceMemory) ? deviceMemory : 8;
    this.viewportWidth = viewportWidth;
    this.requestedParticles = requestedParticles;
    this.warmupMs = warmupMs;
    this.cooldownMs = cooldownMs;
    this.downgradeSamples = downgradeSamples;
    this.upgradeSamples = upgradeSamples;
    this.lowSamples = 0;
    this.highSamples = 0;
    this.lastChangeAt = startedAt;
    this.warmupUntil = startedAt + warmupMs;

    this.level = selectInitialLevel({
      isWebGL2,
      hardwareConcurrency: this.hardwareConcurrency,
      deviceMemory: this.deviceMemory,
      viewportWidth,
    });
    this.maximumLevel = this.level;
  }

  setRequestedParticles(value) {
    this.requestedParticles = clamp(Math.round(value), 50_000, 150_000);
    return this.getParticleCount();
  }

  getParticleCount() {
    return Math.min(
      this.requestedParticles,
      QUALITY_PROFILES[this.level].particleCap,
    );
  }

  getPixelRatio(devicePixelRatio = 1) {
    return clamp(
      devicePixelRatio,
      1,
      QUALITY_PROFILES[this.level].dprCap,
    );
  }

  sample(fps, now) {
    if (!Number.isFinite(fps) || now < this.warmupUntil) {
      return null;
    }
    if (now - this.lastChangeAt < this.cooldownMs) {
      return null;
    }

    if (fps < 38) {
      this.lowSamples += 1;
      this.highSamples = 0;
    } else if (fps > 57) {
      this.highSamples += 1;
      this.lowSamples = 0;
    } else {
      this.lowSamples = 0;
      this.highSamples = 0;
    }

    if (this.lowSamples >= this.downgradeSamples && this.level > 0) {
      this.level -= 1;
      this.commitLevelChange(now);
      return this.getSnapshot();
    }
    if (
      this.highSamples >= this.upgradeSamples &&
      this.level < this.maximumLevel
    ) {
      this.level += 1;
      this.commitLevelChange(now);
      return this.getSnapshot();
    }
    return null;
  }

  resetSampling(now) {
    this.lowSamples = 0;
    this.highSamples = 0;
    this.warmupUntil = now + Math.min(this.warmupMs, 1_500);
  }

  commitLevelChange(now) {
    this.lowSamples = 0;
    this.highSamples = 0;
    this.lastChangeAt = now;
  }

  getSnapshot() {
    const profile = QUALITY_PROFILES[this.level];
    return {
      ...profile,
      level: this.level,
      requestedParticles: this.requestedParticles,
      particleCount: this.getParticleCount(),
    };
  }
}

function selectInitialLevel({
  isWebGL2,
  hardwareConcurrency,
  deviceMemory,
  viewportWidth,
}) {
  if (!isWebGL2 || hardwareConcurrency <= 2 || deviceMemory <= 2) {
    return 0;
  }
  if (
    viewportWidth < 720 ||
    hardwareConcurrency <= 4 ||
    deviceMemory <= 4
  ) {
    return 1;
  }
  if (
    viewportWidth >= 1100 &&
    hardwareConcurrency >= 8 &&
    deviceMemory >= 8
  ) {
    return 3;
  }
  return 2;
}
